import React, { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';

const Chevron = ({ open }) => <span className="inline-block w-4 text-[#00D4FF]">{open ? '▾' : '▸'}</span>;
const numCls = 'w-16 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white disabled:opacity-40';

// Super-admin User Management: collapsible Association -> Brokerage -> brokers/agents,
// plus a "No association" section for unaffiliated app brokerages (with an Assign action),
// and a per-association "+ Brokerage" manual-create.
export default function AssociationTree() {
  const [assocs, setAssocs] = useState([]);
  const [unaff, setUnaff] = useState([]);
  const [error, setError] = useState('');
  const [k, setK] = useState(0); // bump to remount nodes after a change

  const loadAssocs = useCallback(() => authFetch(`${API}/api/associations`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load')))).then(setAssocs).catch((e) => setError(e.message)), []);
  const loadUnaff = useCallback(() => authFetch(`${API}/api/associations/admin/unaffiliated-brokerages`)
    .then((r) => (r.ok ? r.json() : [])).then(setUnaff).catch(() => {}), []);
  useEffect(() => { loadAssocs(); loadUnaff(); }, [loadAssocs, loadUnaff]);
  const refresh = () => { loadAssocs(); loadUnaff(); setK((x) => x + 1); };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        <h1 className="text-white text-3xl font-bold mb-1">User Management</h1>
        <p className="text-[#B0BEC5] text-sm mb-6">Associations &rarr; brokerages &rarr; brokers &amp; agents. Click a row to expand.</p>
        {error && <p className="text-red-400">{error}</p>}
        {assocs.length === 0 && !error && <p className="text-[#B0BEC5]">No associations yet.</p>}
        <div className="space-y-2">{assocs.map((a) => <AssocNode key={`${a.id}-${k}`} a={a} onChanged={refresh} />)}</div>
        <NoAssociationSection unaff={unaff} assocs={assocs} onChanged={refresh} />
      </div>
    </div>
  );
}

function AssocNode({ a, onChanged }) {
  const [open, setOpen] = useState(false);
  const [brokerages, setBrokerages] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => authFetch(`${API}/api/associations/${a.id}/brokerages`).then((r) => (r.ok ? r.json() : [])).then(setBrokerages);
  const toggle = () => { const nx = !open; setOpen(nx); if (nx && brokerages === null) load(); };
  return (
    <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30">
      <div className="w-full flex flex-wrap items-center gap-2 px-4 py-3">
        <button onClick={toggle} className="flex items-center gap-2 text-left min-w-0">
          <Chevron open={open} />
          <span className="text-white font-semibold min-w-0 break-words">{a.display_name}</span>
          <span className="text-gray-500 text-xs min-w-0 break-words">/ {a.name}</span>
        </button>
        <span className="ml-auto shrink-0 text-xs text-[#B0BEC5]">{[a.licensed_re && 'RE', a.licensed_cre && 'CRE'].filter(Boolean).join('+')} &middot; {a.status}</span>
        <button onClick={() => { setOpen(true); if (brokerages === null) load(); setCreating(!creating); }}
          className="shrink-0 px-2 py-1 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">{creating ? 'Cancel' : '+ Brokerage'}</button>
      </div>
      {creating && <CreateBrokerageForm a={a} reload={load} />}
      {open && (
        <div className="pl-6 pr-3 pb-3 space-y-1">
          {brokerages === null ? <p className="text-[#B0BEC5] text-xs px-2 py-1">Loading&hellip;</p>
            : brokerages.length === 0 ? <p className="text-[#B0BEC5] text-xs px-2 py-1">No brokerages yet.</p>
              : brokerages.map((b) => <BrokerageNode key={b.id} aid={a.id} b={b} />)}
        </div>
      )}
    </div>
  );
}

function CreateBrokerageForm({ a, reload }) {
  const [f, setF] = useState({ name: '', broker_name: '', broker_email: '',
    want_re: !!a.licensed_re, want_cre: !!a.licensed_cre, seats_re: 1, seats_cre: 1 });
  const [invite, setInvite] = useState(''); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const set = (kk, v) => setF({ ...f, [kk]: v });
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setInvite('');
    if (!(f.want_re || f.want_cre)) { setErr('Enable at least one product.'); return; }
    setBusy(true);
    const body = { ...f, seats_re: f.want_re ? Number(f.seats_re) || 0 : 0, seats_cre: f.want_cre ? Number(f.seats_cre) || 0 : 0 };
    const res = await authFetch(`${API}/api/associations/${a.id}/brokerages`, { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({})); setBusy(false);
    if (!res.ok) { setErr(d.detail || 'Create failed'); return; }
    if (d.invite_path) setInvite(`${window.location.origin}${d.invite_path}`);
    setF({ ...f, name: '', broker_name: '', broker_email: '' });
    reload();
  };
  return (
    <form onSubmit={submit} className="mx-4 mb-3 p-3 bg-[#0A0E27] rounded border border-[#546E7A]/30 space-y-2">
      <p className="text-xs text-[#B0BEC5]">Create a new brokerage under <span className="text-white">{a.display_name}</span> (same as an approved signup).</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input required placeholder="Brokerage name *" value={f.name} onChange={(e) => set('name', e.target.value)} className="px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm" />
        <input required placeholder="Principal broker name *" value={f.broker_name} onChange={(e) => set('broker_name', e.target.value)} className="px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm" />
        <input required type="email" placeholder="Broker email *" value={f.broker_email} onChange={(e) => set('broker_email', e.target.value)} className="px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm" />
      </div>
      <div className="flex flex-wrap gap-3">
        {a.licensed_re && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.want_re} onChange={(e) => set('want_re', e.target.checked)} /> RE seats <input type="number" min="0" value={f.seats_re} disabled={!f.want_re} onChange={(e) => set('seats_re', e.target.value)} className={numCls} /></label>}
        {a.licensed_cre && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.want_cre} onChange={(e) => set('want_cre', e.target.checked)} /> CRE seats <input type="number" min="0" value={f.seats_cre} disabled={!f.want_cre} onChange={(e) => set('seats_cre', e.target.value)} className={numCls} /></label>}
      </div>
      {err && <p className="text-red-400 text-xs">{err}</p>}
      <button type="submit" disabled={busy} className="px-3 py-1.5 text-sm bg-[#00E676] text-[#0A0E27] font-semibold rounded disabled:opacity-50">{busy ? 'Creating…' : 'Create & Invite'}</button>
      {invite && (
        <div className="mt-1">
          <p className="text-xs text-[#B0BEC5] mb-1">Invite link (email is stubbed — send this to the broker):</p>
          <div className="flex gap-2 items-center">
            <input readOnly value={invite} className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-[#00D4FF] text-xs" />
            <button type="button" onClick={() => navigator.clipboard && navigator.clipboard.writeText(invite)} className="px-3 py-1.5 text-sm bg-[#00D4FF] text-[#0A0E27] font-semibold rounded shrink-0">Copy</button>
          </div>
        </div>
      )}
    </form>
  );
}

const CopyLink = ({ value }) => (
  <span className="inline-flex gap-1 items-center max-w-full">
    <input readOnly value={value} className="min-w-0 flex-1 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-[#00D4FF] text-[11px]" />
    <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(value)} className="px-2 py-0.5 text-[11px] bg-[#00D4FF] text-[#0A0E27] font-semibold rounded shrink-0">Copy</button>
  </span>
);

function BrokerageNode({ aid, b }) {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  return (
    <div className="border-l border-[#546E7A]/30">
      <div className="w-full flex flex-wrap items-center gap-2 px-3 py-2">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left min-w-0">
          <Chevron open={open} />
          <span className="text-white text-sm min-w-0 break-words">{b.name}</span>
        </button>
        <span className="ml-auto shrink-0 text-xs text-[#B0BEC5]">
          {[b.licensed_re && `RE ${b.seats_re}`, b.licensed_cre && `CRE ${b.seats_cre}`].filter(Boolean).join(' · ')} &middot; {b.subscription_status} &middot; {b.status}
        </span>
        <button onClick={() => { setManage(!manage); setOpen(true); }}
          className="shrink-0 px-2 py-1 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">{manage ? 'Done' : 'Manage'}</button>
      </div>
      {open && (manage ? <BrokerageManagePanel aid={aid} bid={b.id} /> : <BrokerageReadOnlyUsers aid={aid} bid={b.id} />)}
    </div>
  );
}

function BrokerageReadOnlyUsers({ aid, bid }) {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    authFetch(`${API}/api/associations/${aid}/brokerages/${bid}/users`).then((r) => (r.ok ? r.json() : [])).then(setUsers);
  }, [aid, bid]);
  return (
    <div className="pl-6 pr-2 pb-2">
      {users === null ? <p className="text-[#B0BEC5] text-xs px-2 py-1">Loading&hellip;</p> : (
        <>
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-2 px-3 py-1 text-xs">
              <span className={u.is_active ? 'text-white min-w-0 break-words' : 'text-gray-500 line-through min-w-0 break-words'}>{u.full_name}</span>
              <span className="text-gray-500 min-w-0 break-words">({u.email})</span>
              <span className="px-1.5 py-0.5 rounded bg-[#243B55] text-[#B0BEC5] shrink-0">{u.role}</span>
              <span className="text-[#B0BEC5] shrink-0">{[u.access_re && 'RE', u.access_cre && 'CRE'].filter(Boolean).join('+') || '—'}</span>
              {u.invite_pending && <span className="text-orange-400 shrink-0">invite pending</span>}
            </div>
          ))}
          <p className="text-xs text-gray-500 px-3 pt-1">Use <span className="text-[#00D4FF]">Manage</span> to add users, adjust seats, offboard, or add clients.</p>
        </>
      )}
    </div>
  );
}

// Super-admin (god mode) brokerage management — flows through the SAME /admin provisioning + seat
// endpoints as the Brokerage console (no bypass).
function BrokerageManagePanel({ aid, bid }) {
  const api = `/api/associations/admin/brokerages/${bid}`;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [invites, setInvites] = useState([]);
  const load = () => authFetch(`${API}${api}/console`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load')))).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);
  const addInvite = (name, path) => setInvites((v) => [{ name, link: `${window.location.origin}${path}` }, ...v]);
  if (err) return <p className="text-red-400 text-xs px-3 py-1">{err}</p>;
  if (!data) return <p className="text-[#B0BEC5] text-xs px-3 py-1">Loading&hellip;</p>;
  const s = data.seats;
  return (
    <div className="pl-4 sm:pl-6 pr-2 pb-3 space-y-2">
      <MgmtSeats aid={aid} bid={bid} s={s} reload={load} />
      <MgmtCreateUser api={api} s={s} reload={load} addInvite={addInvite} />
      {invites.length > 0 && (
        <div className="bg-[#0A0E27] rounded p-2 border border-[#546E7A]/30">
          <p className="text-[11px] text-[#B0BEC5] mb-1">Invite links (email stubbed — send these):</p>
          {invites.map((iv, i) => <div key={i} className="mb-1"><div className="text-[11px] text-gray-400">{iv.name}</div><CopyLink value={iv.link} /></div>)}
        </div>
      )}
      <div>{data.users.map((u) => <MgmtUserRow key={u.id} api={api} u={u} users={data.users} reload={load} />)}</div>
    </div>
  );
}

function MgmtSeats({ aid, bid, s, reload }) {
  const [re, setRe] = useState(s.cap_re); const [cre, setCre] = useState(s.cap_cre);
  const [msg, setMsg] = useState('');
  const save = async () => {
    setMsg('');
    const body = {};
    if (s.licensed_re) body.seats_re = Number(re) || 0;
    if (s.licensed_cre) body.seats_cre = Number(cre) || 0;
    const r = await authFetch(`${API}/api/associations/${aid}/brokerages/${bid}`, { method: 'PATCH', body: JSON.stringify(body) });
    if (r.ok) { setMsg('Caps updated.'); reload(); } else setMsg('Failed');
  };
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      {s.licensed_re && <span className="text-[#B0BEC5]">RE seats <span className="text-white">{s.used_re}</span> / <input type="number" min="0" value={re} onChange={(e) => setRe(e.target.value)} className="w-14 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white" /></span>}
      {s.licensed_cre && <span className="text-[#B0BEC5]">CRE seats <span className="text-white">{s.used_cre}</span> / <input type="number" min="0" value={cre} onChange={(e) => setCre(e.target.value)} className="w-14 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white" /></span>}
      <button onClick={save} className="px-2 py-1 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded shrink-0">Save caps</button>
      {msg && <span className="text-[#00E676]">{msg}</span>}
    </div>
  );
}

function MgmtCreateUser({ api, s, reload, addInvite }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ full_name: '', email: '', role: 'agent',
    access_re: !!s.licensed_re && !s.licensed_cre, access_cre: !!s.licensed_cre && !s.licensed_re });
  const [err, setErr] = useState('');
  const set = (k, v) => setF({ ...f, [k]: v });
  const isAdmin = f.role === 'brokerage_admin';
  const create = async (e) => {
    e.preventDefault(); setErr('');
    if (!isAdmin && !(f.access_re || f.access_cre)) { setErr('Select at least one product.'); return; }
    const r = await authFetch(`${API}${api}/users`, { method: 'POST', body: JSON.stringify(f) });
    const d = await r.json().catch(() => ({}));
    if (r.status === 409) { setErr(`No seats left for ${d.detail === 'AT_CAP_RE' ? 'RE' : 'CRE'}. Raise the cap above, then retry.`); return; }
    if (!r.ok) { setErr(d.detail || 'Failed'); return; }
    addInvite(f.full_name, d.invite_path);
    setF({ ...f, full_name: '', email: '' }); reload();
  };
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="px-2 py-1 text-xs text-[#00D4FF] hover:bg-[#243B55] rounded">{open ? 'Cancel' : '+ Add user'}</button>
      {open && (
        <form onSubmit={create} className="mt-1 p-2 bg-[#0A0E27] rounded border border-[#546E7A]/30 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input required placeholder="Full name *" value={f.full_name} onChange={(e) => set('full_name', e.target.value)} className="px-2 py-1 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-xs" />
            <input required type="email" placeholder="Email *" value={f.email} onChange={(e) => set('email', e.target.value)} className="px-2 py-1 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-xs" />
            <select value={f.role} onChange={(e) => set('role', e.target.value)} className="px-2 py-1 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-xs">
              <option value="agent">Agent</option>
              <option value="broker">Broker</option>
              <option value="brokerage_admin">Brokerage Admin — no seat</option>
            </select>
          </div>
          {!isAdmin && (
            <div className="flex flex-wrap gap-3 text-xs text-white">
              {s.licensed_re && <label className="flex items-center gap-1"><input type="checkbox" checked={f.access_re} onChange={(e) => set('access_re', e.target.checked)} /> RE seat</label>}
              {s.licensed_cre && <label className="flex items-center gap-1"><input type="checkbox" checked={f.access_cre} onChange={(e) => set('access_cre', e.target.checked)} /> CRE seat</label>}
            </div>
          )}
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <button type="submit" className="px-3 py-1 text-xs bg-[#00E676] text-[#0A0E27] font-semibold rounded">Create &amp; Invite</button>
        </form>
      )}
    </div>
  );
}

function MgmtUserRow({ api, u, users, reload }) {
  const [off, setOff] = useState(false); const [target, setTarget] = useState('');
  const [addcli, setAddcli] = useState(false); const [msg, setMsg] = useState('');
  const peers = (users || []).filter((x) => x.is_active && x.id !== u.id && !x.invite_pending);
  const patch = async (body) => {
    const r = await authFetch(`${API}${api}/users/${u.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); alert((d.detail === 'AT_CAP_RE' || d.detail === 'AT_CAP_CRE') ? 'No seats available for that product — raise the cap first.' : (d.detail || 'Failed')); }
    reload();
  };
  const offboard = async () => {
    setMsg('');
    const body = target ? { reassign_to_uid: Number(target) } : {};
    const r = await authFetch(`${API}${api}/users/${u.id}/offboard`, { method: 'POST', body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Failed'); return; }
    setOff(false); reload();
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-2 py-1 text-xs border-b border-[#546E7A]/15 last:border-0">
      <span className={u.is_active ? 'text-white min-w-0 break-words' : 'text-gray-500 line-through min-w-0 break-words'}>{u.full_name}</span>
      <span className="text-gray-500 min-w-0 break-words">({u.email})</span>
      <span className="px-1.5 py-0.5 rounded bg-[#243B55] text-[#B0BEC5] shrink-0">{u.role}</span>
      <span className="text-[#B0BEC5] shrink-0">{[u.access_re && 'RE', u.access_cre && 'CRE'].filter(Boolean).join('+') || '—'}</span>
      {u.invite_pending && <span className="text-orange-400 shrink-0">invite pending</span>}
      <span className="ml-auto flex flex-wrap gap-1 shrink-0 justify-end">
        {u.is_active ? (
          <>
            <button onClick={() => patch({ is_active: false })} className="px-1.5 py-0.5 bg-red-600/80 text-white rounded">Deactivate</button>
            <button onClick={() => setOff(!off)} className="px-1.5 py-0.5 bg-orange-600/80 text-white rounded">Offboard</button>
          </>
        ) : (
          <button onClick={() => patch({ is_active: true })} className="px-1.5 py-0.5 bg-[#00E676] text-[#0A0E27] rounded">Reactivate</button>
        )}
        {u.app_agent_id && <button onClick={() => setAddcli(!addcli)} className="px-1.5 py-0.5 bg-[#243B55] text-[#00D4FF] rounded">Add client</button>}
      </span>
      {off && (
        <div className="w-full mt-1 flex flex-wrap items-center gap-2 bg-[#0A0E27] rounded p-2 border border-[#546E7A]/30">
          <span className="text-[#B0BEC5]">Reassign book to:</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="min-w-0 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white">
            <option value="">— Don’t reassign —</option>
            {peers.map((p) => <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>)}
          </select>
          <button onClick={offboard} className="px-2 py-0.5 bg-orange-600 text-white font-semibold rounded">Confirm offboard</button>
          {msg && <span className="text-red-400">{msg}</span>}
        </div>
      )}
      {addcli && u.app_agent_id && <AddClientForm appAgentId={u.app_agent_id} onDone={() => setAddcli(false)} />}
    </div>
  );
}

function AddClientForm({ appAgentId, onDone }) {
  const [name, setName] = useState(''); const [email, setEmail] = useState('');
  const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg('');
    const r = await authFetch(`${API}/api/buyers/for-agent/${appAgentId}`, { method: 'POST', body: JSON.stringify({ name, email }) });
    const d = await r.json().catch(() => ({})); setBusy(false);
    if (!r.ok) { setMsg(d.detail || 'Failed'); return; }
    setMsg(`Created ${name}.`); setName(''); setEmail('');
  };
  return (
    <form onSubmit={submit} className="w-full mt-1 flex flex-wrap items-center gap-2 bg-[#0A0E27] rounded p-2 border border-[#546E7A]/30">
      <span className="text-[#B0BEC5]">New client for this agent:</span>
      <input required placeholder="Name *" value={name} onChange={(e) => setName(e.target.value)} className="min-w-0 px-2 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white" />
      <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="min-w-0 px-2 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white" />
      <button type="submit" disabled={busy} className="px-2 py-0.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded disabled:opacity-50">{busy ? '…' : 'Create'}</button>
      <button type="button" onClick={onDone} className="px-2 py-0.5 text-[#B0BEC5]">Close</button>
      {msg && <span className="text-[#00E676]">{msg}</span>}
    </form>
  );
}

function NoAssociationSection({ unaff, assocs, onChanged }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-4">
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-2 px-4 py-3 text-left bg-[#1A2744] rounded-lg border border-dashed border-[#546E7A]/50">
        <Chevron open={open} />
        <span className="text-[#B0BEC5] font-semibold">No association</span>
        <span className="ml-auto shrink-0 text-xs text-gray-500">{unaff.length} unaffiliated brokerage{unaff.length !== 1 ? 's' : ''}</span>
      </button>
      {open && (
        <div className="pl-4 sm:pl-6 pr-2 py-2 space-y-1">
          <p className="text-xs text-gray-500 px-2">Brokerages not attached to any association (direct/unaffiliated customers). Assign one to bring it under an association.</p>
          {unaff.length === 0 ? <p className="text-gray-500 text-xs px-2 py-1">Every brokerage is attached to an association.</p>
            : unaff.map((b) => <UnaffiliatedNode key={b.id} b={b} assocs={assocs} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

function UnaffiliatedNode({ b, assocs, onChanged }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [assign, setAssign] = useState(false);
  const toggle = () => {
    const nx = !open; setOpen(nx);
    if (nx && users === null)
      authFetch(`${API}/api/associations/admin/unaffiliated-brokerages/${b.id}/users`).then((r) => (r.ok ? r.json() : [])).then(setUsers);
  };
  return (
    <div className="border-l border-[#546E7A]/30">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button onClick={toggle} className="flex items-center gap-2 text-left min-w-0">
          <Chevron open={open} />
          <span className="text-white text-sm min-w-0 break-words">{b.name}</span>
        </button>
        <span className="text-xs text-gray-500 shrink-0">{b.agent_count} agent{b.agent_count !== 1 ? 's' : ''}</span>
        <button onClick={() => { setAssign(!assign); if (!open) toggle(); }}
          className="ml-auto shrink-0 px-2 py-1 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">{assign ? 'Cancel' : 'Assign to association'}</button>
      </div>
      {open && users !== null && (
        <div className="pl-6 pr-2 pb-1">
          {users.length === 0 ? <p className="text-gray-500 text-xs px-2 py-1">No agents.</p> : users.map((u) => (
            <div key={u.agent_id} className="flex flex-wrap items-center gap-2 px-3 py-1 text-xs">
              <span className="text-white min-w-0 break-words">{u.full_name}</span>
              <span className="text-gray-500 min-w-0 break-words">({u.email || 'no email'})</span>
              <span className="px-1.5 py-0.5 rounded bg-[#243B55] text-[#B0BEC5] shrink-0">{u.role}</span>
              {!u.has_login && <span className="text-orange-400 shrink-0">no login</span>}
            </div>
          ))}
        </div>
      )}
      {assign && <AssignForm b={b} users={users} assocs={assocs} onDone={() => { setAssign(false); onChanged(); }} />}
    </div>
  );
}

function AssignForm({ b, users, assocs, onDone }) {
  const nAgents = (users || []).length || b.agent_count || 0;
  const [aid, setAid] = useState('');
  const [re, setRe] = useState(false); const [cre, setCre] = useState(false);
  const [seatsRe, setSeatsRe] = useState(nAgents || 1); const [seatsCre, setSeatsCre] = useState(nAgents || 1);
  const [trial, setTrial] = useState(true); const [days, setDays] = useState(14);
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const assoc = assocs.find((a) => String(a.id) === String(aid));
  const pick = (v) => {
    setAid(v);
    const a = assocs.find((x) => String(x.id) === String(v));
    setRe(!!(a && a.licensed_re));
    setCre(!!(a && a.licensed_cre));
  };
  const submit = async () => {
    setErr('');
    if (!assoc) { setErr('Choose an association.'); return; }
    if (!(re || cre)) { setErr('Enable at least one product.'); return; }
    setBusy(true);
    const res = await authFetch(`${API}/api/associations/admin/assign-brokerage`, {
      method: 'POST',
      body: JSON.stringify({ app_brokerage_id: b.id, association_id: assoc.id,
        licensed_re: re, seats_re: Number(seatsRe) || 0, licensed_cre: cre, seats_cre: Number(seatsCre) || 0,
        trial_days: trial ? (Number(days) || null) : 0 }),
    });
    const d = await res.json().catch(() => ({})); setBusy(false);
    if (!res.ok) { setErr(d.detail || 'Assign failed'); return; }
    onDone();
  };
  return (
    <div className="ml-4 sm:ml-6 mr-2 mb-2 p-3 bg-[#0A0E27] rounded border border-[#546E7A]/30 space-y-2">
      <div>
        <label className="block text-xs text-[#B0BEC5] mb-1">Association</label>
        <select value={aid} onChange={(e) => pick(e.target.value)} className="w-full px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm">
          <option value="">Choose…</option>
          {assocs.map((a) => <option key={a.id} value={a.id}>{a.display_name} ({[a.licensed_re && 'RE', a.licensed_cre && 'CRE'].filter(Boolean).join('+')})</option>)}
        </select>
      </div>
      {assoc && (
        <>
          <div className="flex flex-wrap gap-3">
            {assoc.licensed_re && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={re} onChange={(e) => setRe(e.target.checked)} /> RE seats <input type="number" min="0" value={seatsRe} disabled={!re} onChange={(e) => setSeatsRe(e.target.value)} className={numCls} /></label>}
            {assoc.licensed_cre && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={cre} onChange={(e) => setCre(e.target.checked)} /> CRE seats <input type="number" min="0" value={seatsCre} disabled={!cre} onChange={(e) => setSeatsCre(e.target.value)} className={numCls} /></label>}
          </div>
          <label className="flex flex-wrap items-center gap-2 text-sm text-white">
            <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} /> Start as trial
            <input type="number" min="0" value={days} disabled={!trial} onChange={(e) => setDays(e.target.value)} className={numCls} /> days
            {!trial && <span className="text-xs text-gray-500">(billable immediately)</span>}
          </label>
          <p className="text-xs text-gray-500">{nAgents} existing agent{nAgents !== 1 ? 's' : ''} will be registered as seat holders; their current logins keep working.</p>
        </>
      )}
      {err && <p className="text-red-400 text-xs">{err}</p>}
      <button onClick={submit} disabled={busy} className="px-3 py-1.5 text-sm bg-[#00E676] text-[#0A0E27] font-semibold rounded disabled:opacity-50">{busy ? 'Assigning…' : 'Confirm assignment'}</button>
    </div>
  );
}
